import { useState, useEffect, useRef, useMemo } from 'react'
import type { FC } from 'react'
import { ProviderListItem, ProviderDetailWithAdditionalData } from './ProviderListItem'
import { useDispatch } from 'react-redux'
import { queueNotification } from '~/shared/notifications/actions'
import { SteerageProvider } from '~/entities/steerage'
import { CPTCode } from './types'
import { PlaceOfService } from './utils'
import type { Person } from '~/shared/types'
import {
  referralSearchRequest,
  referralSearchTalonProcedures,
  type ReferralSearchTalonProceduresCreateBodyParams,
  type StatesListResponse,
} from '@fireflyhealth/api'
import { useQuery } from '@tanstack/react-query'
import { useQuery as useApiQuery } from '~/shared/hooks/query'
import { Box } from '@mui/material'
import { EmptyState, SegmentedControl } from '~/shared/ui'
import { haveChanged } from '~/shared/lib/haveChanged'
const createProcedureSearchRequestKey = 'createProcedureSearchRequest'

const useCreateProcedureSearchRequest = (
  payload: ReferralSearchTalonProceduresCreateBodyParams,
) => {
  return useApiQuery(
    // We are caching the search results based on the Payload. If the payload changes, the search
    // results will be persisted else it will use the cache data.
    [createProcedureSearchRequestKey, payload],
    async () => {
      const response = await referralSearchTalonProcedures.create({ data: payload })
      return response.data
    },
    {
      staleTime: Infinity,
    },
    {
      error: `Something went wrong while doing procedure search`,
    },
  )
}

const PROVIDER_SEARCH_PAGE_SIZE_HUNDRED = 100

type SiteFilter = 'all' | 'freestanding'

// A result is a hospital site when the vendor tags any of its location_types
// as "Hospital"; every other tag (Imaging, Other, ...) counts as freestanding.
const isHospitalSite = (provider: ProviderDetailWithAdditionalData): boolean =>
  provider.location_types?.some(type => type?.trim().toLowerCase() === 'hospital') ?? false

interface ProviderListProps {
  person: Person
  zipCode: string | null | undefined
  distance: number
  cptCode: CPTCode | null
  placeOfServices?: PlaceOfService[]
  steerageId: number | null
  resultCountCallback: (val: number) => void
  loadingResultsCallback: (val: boolean) => void
  steerageProviderList?: Array<SteerageProvider>
  states?: StatesListResponse
  providerSearchType: 'provider' | 'facility' | 'procedure'
  searchAvailabilityId: number | undefined
}

const ProcedureFacilityList: FC<ProviderListProps> = (props: ProviderListProps) => {
  const dispatch = useDispatch()

  const { resultCountCallback, loadingResultsCallback } = props

  // To show the total results on the parent component
  const [resultCount, setResultCount] = useState<number | null>(null)
  // Stores the search results
  const [providers, setProviders] = useState<ProviderDetailWithAdditionalData[]>([])
  // To handle the pollable job
  const [jobId, setJobId] = useState<number | null>(null)
  // Client-side view filter over the fetched results (hospital vs freestanding)
  const [siteFilter, setSiteFilter] = useState<SiteFilter>('all')

  const timeoutRef = useRef<NodeJS.Timeout | null>(null)

  // Memoize the payload to prevent unnecessary re-renders and API calls
  const payload = useMemo<ReferralSearchTalonProceduresCreateBodyParams>(() => {
    return {
      distance: props.distance,
      person: props.person.id,
      cpt_code: props.cptCode?.id ?? null,
      page: 0,
      page_size: PROVIDER_SEARCH_PAGE_SIZE_HUNDRED,
      address: props.zipCode ?? null,
      steerage: props?.steerageId ?? null,
      search_availability: props.searchAvailabilityId ?? null,
      place_of_service_codes: props.placeOfServices?.map(pos => pos.code) ?? [],
    }
  }, [
    props.distance,
    props.person.id,
    props.cptCode?.id,
    props.zipCode,
    props.steerageId,
    props.searchAvailabilityId,
    props.placeOfServices,
  ])

  const { data: searchRequest, isPending: isProcedureSearchLoading } =
    useCreateProcedureSearchRequest(payload)

  const { isError: isPollFailed, isFetching: isPolling } = useQuery({
    queryKey: referralSearchRequest.retrieveQueryKey({ id: jobId as number }),
    queryFn: async () => {
      const response = await referralSearchRequest.retrieve({ id: jobId as number })
      const data = response.data
      if (data.status === 'failed') {
        // If the request is failed then reset the job id and through an error message
        setJobId(null)
        dispatch(
          queueNotification({
            message: `Search failed. Please try again.`,
            variant: 'error',
          }),
        )
      }
      if (data.status === 'complete') {
        setJobId(null)
        // The @fireflyhealth/api client now types `results` as the search-result
        // row shape (snake_case) that ProviderListItem reads directly.
        setProviders(data.results ?? [])
        setResultCount(data.records_available ?? null)
      }
      return data
    },
    enabled: jobId !== null,
    refetchInterval: jobId ? 250 : false,
    refetchIntervalInBackground: true,
    retry: 1,
  })
  // A failed poll stops polling; a cached failure refetching on return to its job is not one yet.
  if (isPollFailed && !isPolling && jobId !== null) setJobId(null)

  const visibleProviders = useMemo(
    () => (siteFilter === 'freestanding' ? providers.filter(p => !isHospitalSite(p)) : providers),
    [providers, siteFilter],
  )

  // Always return the visible result count to the caller; with the freestanding
  // filter active the header count reflects the client-side filtered rows.
  useEffect(() => {
    if (resultCount == null) {
      resultCountCallback(0)
    } else {
      resultCountCallback(siteFilter === 'freestanding' ? visibleProviders.length : resultCount)
    }
  }, [resultCount, siteFilter, visibleProviders.length, resultCountCallback])

  // Each search request starts polling for its results, including a cached one the terms return to.
  const [startedSearchRequest, setStartedSearchRequest] = useState<typeof searchRequest>()
  if (searchRequest !== startedSearchRequest) {
    setStartedSearchRequest(searchRequest)
    if (searchRequest?.id) setJobId(searchRequest.id)
  }

  useEffect(() => {
    if (jobId) {
      timeoutRef.current = setTimeout(() => {
        // Reset the job id, disabling polling
        setJobId(null)
        // Show a toast to the user
        dispatch(
          queueNotification({
            message: `Search timed out. Please try again.`,
            variant: 'error',
          }),
        )
      }, 30_000)
    } else {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
        timeoutRef.current = null // Reset the ref when clearing
      }
    }
  }, [jobId, dispatch])

  // Show loader at the search results screen and result count section
  const loadingProviders = jobId !== null || isProcedureSearchLoading
  useEffect(() => {
    loadingResultsCallback(loadingProviders)
  }, [loadingProviders, loadingResultsCallback])

  // A change to any search term clears the previous search's results.
  const searchTerms = [props.cptCode, props.distance, props.zipCode, props.placeOfServices]
  const [previousSearchTerms, setPreviousSearchTerms] = useState(searchTerms)
  if (haveChanged(previousSearchTerms, searchTerms)) {
    setPreviousSearchTerms(searchTerms)
    setProviders([])
    setResultCount(null)
  }

  return (
    <Box sx={{ flex: 1, overflow: 'auto' }}>
      {resultCount != null && resultCount > 0 && searchRequest && (
        <>
          <Box
            sx={theme => ({
              position: 'sticky',
              top: 0,
              zIndex: 1,
              display: 'flex',
              alignItems: 'center',
              px: 3,
              py: 1,
              bgcolor: 'background.paper',
              borderBottom: `1px solid ${theme.palette.divider}`,
            })}
          >
            <SegmentedControl
              aria-label="Site type"
              value={siteFilter}
              onChange={setSiteFilter}
              options={[
                { value: 'all', label: 'All sites' },
                { value: 'freestanding', label: 'Freestanding sites' },
              ]}
            />
          </Box>
          {visibleProviders.map(provider => (
            <ProviderListItem
              key={provider?.id}
              provider={provider}
              locationType={null}
              npi={null}
              providerName={null}
              facilityName={null}
              steerageProviderList={props.steerageProviderList}
              steerageID={props.steerageId}
              states={props.states}
              specialtyGroupSearchCategory={null}
              providerSearchType={props.providerSearchType}
              indexInResults={null}
              searchRequestId={searchRequest.id}
              cptCode={props?.cptCode}
            />
          ))}
          {siteFilter === 'freestanding' && visibleProviders.length === 0 && (
            <EmptyState
              title="No freestanding sites"
              caption="Every result for this search is a hospital location."
            />
          )}
        </>
      )}
    </Box>
  )
}

export default ProcedureFacilityList
